import { Dimensions } from 'types'

export type Renderable = {
    id: string
    zIndex?: number
    draw: (frame: number) => void
}

class Renderer {
    readonly gl: WebGLRenderingContext

    private dimensions: Dimensions
    private nodes: Renderable[] = []
    private lastRenderedProgress: number = 0

    constructor(gl: WebGLRenderingContext) {
        this.gl = gl

        this.dimensions = {
            height: gl.canvas.height,
            width: gl.canvas.width,
        }
    }

    addNode(node: Renderable) {
        const foundIndex = this.nodes.findIndex((n) => n.id === node.id)

        if (foundIndex) {
            this.nodes[foundIndex] = node
        } else {
            this.nodes.push(node)
        }
    }

    removeNode(node: Renderable) {
        this.removeNodeById(node.id)
    }

    removeNodeById(id: string) {
        this.nodes = this.nodes.filter((n) => n.id !== id)
    }

    setNodes(nodes: Renderable[]) {
        this.nodes = nodes
    }

    render(progress?: number) {
        const { dimensions, gl } = this
        const drawProgress = typeof progress !== 'undefined' ? progress : this.lastRenderedProgress
        const nodes = [...this.nodes]

        if (dimensions.width !== gl.canvas.width || dimensions.height !== gl.canvas.height) {
            gl.viewport(0, 0, gl.canvas.width, gl.canvas.height)

            this.dimensions = {
                height: gl.canvas.height,
                width: gl.canvas.width,
            }
        }

        gl.disable(gl.CULL_FACE)
        gl.disable(gl.DEPTH_TEST)
        gl.disable(gl.DITHER)
        gl.disable(gl.POLYGON_OFFSET_FILL)
        gl.disable(gl.SAMPLE_ALPHA_TO_COVERAGE)
        gl.disable(gl.SAMPLE_COVERAGE)
        gl.disable(gl.SCISSOR_TEST)
        gl.disable(gl.STENCIL_TEST)

        gl.enable(gl.BLEND)
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)

        gl.clearColor(0, 0, 0, 0)
        gl.clearDepth(1)
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)

        nodes.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).forEach((n) => n.draw(drawProgress))

        this.lastRenderedProgress = drawProgress
    }

    createTexture(source: TexImageSource) {
        const { gl } = this

        const texture = gl.createTexture()!

        gl.bindTexture(gl.TEXTURE_2D, texture)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source)

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)

        return texture
    }
}

export default Renderer
